import React, { useState } from 'react';
import {
  Stack,
  Text,
  Group,
  Button,
  Paper,
  ActionIcon,
  Collapse,
  Image,
  Popover,
  Box,
  Badge,
} from '@mantine/core';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import {
  IconShoppingCart,
  IconExternalLink,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';

interface CartAddedItem {
  itemName: string;
  productName: string;
  quantity: number;
  reasoning?: string;
  productPageURI?: string;
  imageUrl?: string;
  nutritionalRating?: number;
}

const getFoodHealthColors = (score: number) => {
  if (score < 20) return { fg: '#E4002A', bg: '#FFDBDB' };
  if (score < 50) return { fg: '#FFAB02', bg: '#FFEA9D' };
  if (score < 80) return { fg: '#6EC837', bg: '#BCF390' };
  return { fg: '#0C824B', bg: '#C3F1D6' };
};

const FoodHealthGauge: React.FC<{ score: number; size?: number }> = ({ score, size = 32 }) => {
  const { fg, bg } = getFoodHealthColors(score);
  return (
    <svg viewBox="0 0 36 36" width={size} height={size} aria-label={`FoodHealth Score: ${score}`}>
      <path
        fill="none"
        stroke={bg}
        strokeWidth="3"
        strokeLinecap="round"
        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
      />
      <path
        fill="none"
        stroke={fg}
        strokeWidth="3"
        strokeLinecap="round"
        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
        strokeDasharray={`${score}, 100`}
      />
      <text
        x="18"
        y="22"
        textAnchor="middle"
        fontSize="11"
        fontWeight="bold"
        fill={fg}
      >
        {score}
      </text>
    </svg>
  );
};

const TIER_CONFIG = [
  { label: 'Red', min: 0, max: 19, fg: '#E4002A', bg: '#FFDBDB' },
  { label: 'Yellow', min: 20, max: 49, fg: '#FFAB02', bg: '#FFEA9D' },
  { label: 'Light Green', min: 50, max: 79, fg: '#6EC837', bg: '#BCF390' },
  { label: 'Dark Green', min: 80, max: 100, fg: '#0C824B', bg: '#C3F1D6' },
] as const;

const getNutritionSummary = (avgScore: number): string => {
  if (avgScore >= 80) return 'Excellent nutritional quality — high in fiber, protein, and healthy fats.';
  if (avgScore >= 50) return 'Good nutritional quality — a well-balanced mix of nutrients.';
  if (avgScore >= 20) return 'Moderate nutritional quality — consider swapping a few items for healthier options.';
  return 'Low nutritional quality — consider adding more fruits, veggies, and whole grains.';
};

const NutritionSnapshot: React.FC<{ items: CartAddedItem[] }> = ({ items }) => {
  // Rendered inside the cart results bottom sheet on mobile
  const isBottomSheet = useIsBottomSheet();
  const rated = items.filter((i): i is CartAddedItem & { nutritionalRating: number } => i.nutritionalRating != null);
  if (rated.length === 0) return null;

  const avg = Math.round(rated.reduce((sum, i) => sum + i.nutritionalRating, 0) / rated.length);
  const tierCounts = TIER_CONFIG.map(tier => ({
    ...tier,
    count: rated.filter(i => i.nutritionalRating >= tier.min && i.nutritionalRating <= tier.max).length,
  }));
  const total = rated.length;

  return (
    <Paper p="md" bg="gray.0" radius="lg">
      <Stack gap="sm">
        <Group gap={6} align="center">
          <Text size="sm" c="gray.8" fw={500}>Nutrition snapshot</Text>
          <Popover width={260} position="bottom" withArrow shadow="md" withinPortal={!isBottomSheet}>
            <Popover.Target>
              <ActionIcon size="xs" variant="subtle" color="gray" aria-label="What is FoodHealth Score?">
                <IconInfoCircle size={14} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <Stack gap={6}>
                <Text size="xs" fw={600}>FoodHealth Score (0–100)</Text>
                <Text size="xs" c="gray.7" style={{ lineHeight: 1.4 }}>
                  A nutrition rating by the <a href="https://www.foodhealth.co" target="_blank" rel="noreferrer" style={{ color: 'var(--mantine-color-blue-6)' }}>FoodHealth Company</a>. Higher scores mean better nutritional quality.
                </Text>
                {TIER_CONFIG.slice().reverse().map(tier => (
                  <Group key={tier.label} gap={6} align="center" wrap="nowrap">
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: tier.fg, flexShrink: 0 }} />
                    <Text size="xs" c="gray.6">{tier.min}–{tier.max}</Text>
                  </Group>
                ))}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        </Group>
        <Group gap="md" align="center" wrap="nowrap">
          <FoodHealthGauge score={avg} size={56} />
          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="sm" fw={600} c="gray.9">
              Average score: {avg}
            </Text>
            <Text size="xs" c="gray.6" style={{ lineHeight: 1.4 }}>
              {getNutritionSummary(avg)}
            </Text>
            {rated.length < items.length && (
              <Text size="xs" c="gray.5">
                Based on {rated.length} of {items.length} items
              </Text>
            )}
          </Stack>
        </Group>
        <div
          style={{
            display: 'flex',
            height: 22,
            borderRadius: 11,
            overflow: 'hidden',
            gap: 1,
          }}
        >
          {tierCounts.map(tier =>
            tier.count > 0 ? (
              <div
                key={tier.label}
                style={{
                  flex: tier.count / total,
                  backgroundColor: tier.fg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {tier.count / total >= 0.1 && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#fff' }}>
                    {tier.count}
                  </span>
                )}
              </div>
            ) : null
          )}
        </div>
      </Stack>
    </Paper>
  );
};

interface CartFailedItem {
  itemName: string;
  reason: string;
}

interface CartResults {
  addedItems?: CartAddedItem[];
  failedItems?: CartFailedItem[];
}

interface KrogerCartResultsDrawerProps {
  opened: boolean;
  onClose: () => void;
  cartResults: CartResults | null;
  userProfileDomain?: string;
  onCheckOffAddedIngredients: () => void;
}

export const KrogerCartResultsDrawer: React.FC<KrogerCartResultsDrawerProps> = ({
  opened,
  onClose,
  cartResults,
  userProfileDomain,
  onCheckOffAddedIngredients,
}) => {
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState<number[]>([]);
  const [failedItemsExpanded, setFailedItemsExpanded] = useState(false);

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconShoppingCart size={18} />}
          title="Add to cart results"
        />
      }
      size="md"
    >
      <Stack gap="md">
        {cartResults ? (
          <>
            {cartResults.addedItems && cartResults.addedItems.length > 0 && (
              <>
                <Paper p="md" bg="green.0" radius="lg">
                  <Stack gap="md">
                    <Text size="sm" c="green.8" fw={500}>
                      Successfully added {cartResults.addedItems.length} item{cartResults.addedItems.length !== 1 ? 's' : ''} to your Kroger cart
                    </Text>
                    <Button
                      variant="outline"
                      size="md"
                      color="green"
                      component="a"
                      href={`https://www.${userProfileDomain || "kroger.com"}/cart`}
                      target="_blank"
                      rel="noreferrer"
                      fullWidth
                      rightSection={<IconExternalLink size={18} />}
                    >
                      View Kroger cart
                    </Button>
                    <Button
                      variant="filled"
                      size="md"
                      color="green"
                      onClick={onCheckOffAddedIngredients}
                      fullWidth
                      leftSection={<IconCheck size={18} />}
                    >
                      Mark ingredients as purchased
                    </Button>
                  </Stack>
                </Paper>

                <NutritionSnapshot items={cartResults.addedItems} />

                {cartResults.addedItems.some(item => item.reasoning) && (
                  <Paper p="sm" bg="gray.0" radius="lg">
                    <Stack gap="xs">
                      <Group
                        justify="space-between"
                        align="center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setReasoningExpanded(!reasoningExpanded)}
                      >
                        <Text size="sm" c="gray.8" fw={500}>
                          Product selection details ({cartResults.addedItems.filter(item => item.reasoning).length})
                        </Text>
                        <Text size="xs" c="gray.6" style={{ textDecoration: 'underline' }}>
                          {reasoningExpanded ? 'Hide details' : 'Show details'}
                        </Text>
                      </Group>
                      <Collapse in={reasoningExpanded}>
                        <Stack gap="md" mt="xs">
                          {cartResults.addedItems.filter(item => item.reasoning).map((item, index) => {
                            const productUrl = item.productPageURI && userProfileDomain
                              ? `https://www.${userProfileDomain}${item.productPageURI}`
                              : undefined;

                            return (
                              <Paper key={index} p="sm" bg="white" withBorder radius="lg">
                                <Group gap="sm" align="flex-start" wrap="nowrap">
                                  {(item.imageUrl || item.nutritionalRating != null) && (
                                    <Stack gap="xs" align="center" style={{ flexShrink: 0 }}>
                                      {item.imageUrl && (
                                        <Box style={{ position: 'relative' }}>
                                          <a
                                            href={productUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <Image
                                              src={item.imageUrl}
                                              alt={item.productName}
                                              w={48}
                                              h={48}
                                              radius="md"
                                              fit="contain"
                                            />
                                          </a>
                                          <Badge
                                            size="xs"
                                            circle
                                            color="blue"
                                            style={{
                                              position: 'absolute',
                                              top: -6,
                                              left: -6,
                                              padding: 0,
                                              width: 18,
                                              height: 18,
                                              minWidth: 18,
                                              fontSize: 10,
                                              fontWeight: 700,
                                            }}
                                          >
                                            {item.quantity}
                                          </Badge>
                                        </Box>
                                      )}
                                      {item.nutritionalRating != null && (
                                        <FoodHealthGauge score={item.nutritionalRating} />
                                      )}
                                    </Stack>
                                  )}
                                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                    <Group justify="space-between" align="flex-start">
                                      <Text size="sm" fw={600} c="gray.9">
                                        {item.itemName}
                                      </Text>
                                      {productUrl && (
                                        <ActionIcon
                                          component="a"
                                          href={productUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          size="sm"
                                          variant="subtle"
                                          color="blue"
                                        >
                                          <IconExternalLink size={16} />
                                        </ActionIcon>
                                      )}
                                    </Group>
                                    <Text size="xs" c="gray.7" fw={500}>
                                      {item.productName}
                                    </Text>
                                    {item.reasoning && (
                                      <Text
                                        size="xs"
                                        c="blue.5"
                                        style={{ cursor: 'pointer', lineHeight: 1.4 }}
                                        onClick={() => setExpandedReasoning(prev =>
                                          prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
                                        )}
                                      >
                                        {expandedReasoning.includes(index) ? 'Hide reasoning' : 'Why this?'}
                                      </Text>
                                    )}
                                    <Collapse in={expandedReasoning.includes(index)}>
                                      <Text size="xs" c="gray.6" style={{ lineHeight: 1.4 }}>
                                        {item.reasoning}
                                      </Text>
                                    </Collapse>
                                  </Stack>
                                </Group>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Collapse>
                    </Stack>
                  </Paper>
                )}
              </>
            )}

            {cartResults.failedItems && cartResults.failedItems.length > 0 && (
              <Paper p="sm" bg="red.0" radius="lg">
                <Stack gap="xs">
                  <Group
                    justify="space-between"
                    align="center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setFailedItemsExpanded(!failedItemsExpanded)}
                  >
                    <Text size="sm" c="red.8" fw={500}>
                      Could not add {cartResults.failedItems.length} item{cartResults.failedItems.length !== 1 ? 's' : ''}
                    </Text>
                    <Text size="xs" c="red.6" style={{ textDecoration: 'underline' }}>
                      {failedItemsExpanded ? 'Hide details' : 'Show details'}
                    </Text>
                  </Group>
                  <Collapse in={failedItemsExpanded}>
                    <Stack gap="md" mt="xs">
                      {cartResults.failedItems.slice(0, 10).map((item, index) => (
                        <Paper key={index} p="sm" withBorder radius="lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', borderColor: 'var(--mantine-color-red-2)' }}>
                          <Stack gap={4}>
                            <Text size="sm" fw={600} c="red.8">
                              {item.itemName}
                            </Text>
                            <Text size="xs" c="red.7" style={{ lineHeight: 1.4 }}>
                              {item.reason}
                            </Text>
                          </Stack>
                        </Paper>
                      ))}
                      {cartResults.failedItems.length > 10 && (
                        <Text size="xs" c="red.6" ta="center" mt="xs">
                          + {cartResults.failedItems.length - 10} more items
                        </Text>
                      )}
                    </Stack>
                  </Collapse>
                </Stack>
              </Paper>
            )}

            <Text size="xs" c="dimmed">
              We don't always get it right! Please review your cart to confirm product, brand, and quantity selection before checkout.
            </Text>
          </>
        ) : (
          <Text size="sm" c="dimmed" ta="center">
            No cart results to display.
          </Text>
        )}
      </Stack>
    </ResponsiveDrawer>
  );
};
